import i18n from "utils/i18n";
import PropTypes from "prop-types";
import React from "react";

/**
 * Component for displaying a filter value such as a user or group.
 *
 * @example
 *   <FilterValue value={{ groupId: "active-user", id: 1, label: "Billy Bob" }} />
 */
class FilterValue extends React.Component {
  /**
   * Creates the component with the initial state based on props.
   *
   * @param {object} props Initial props
   */
  constructor(props) {
    super(props);

    // Bind functions to make `this` available
    this.onRemove = this.onRemove.bind(this);
  }

  /**
   * Click handler for the remove icon that calls the `onRemove` callback prop.
   */
  onRemove() {
    this.props.onRemove(this.props.value);
  }

  /**
   * Used by the MultiSelect component to render the HTML to represent a selected value.
   *
   * @returns {object} The React element
   */
  render() {
    const { value } = this.props;

    return (
      <div className={`filter-value ${value.groupId}`} title={this.renderTitle()}>
        <span className="filter-value-icon">{this.renderIcon()}</span>
        <span className="filter-value-label">{value.label}</span>
        {this.renderRemoveButton()}
      </div>
    );
  }

  /**
   * Returns the single character used to represent the selected value.
   *
   * @returns {string} This might be the letter "U" for a user or "G" for a group
   */
  renderIcon() {
    switch (this.props.value.groupId) {
      case "archived-user":
        return i18n.t("report.filter_select_archived_user");
      case "group":
        return i18n.t("report.filter_select_group");
      default:
        return i18n.t("report.filter_select_active_user");
    }
  }

  /**
   * Returns the element for removing the value if the `onRemove` callback prop is given.
   *
   * @returns {object} The React element
   */
  renderRemoveButton() {
    if (this.props.onRemove == null) {
      return;
    }

    /* eslint-disable react/jsx-no-literals */
    return (
      <span className="filter-value-remove" onClick={this.onRemove} title={i18n.t("global.remove")}>
        &times;
      </span>
    );
    /* eslint-enable react/jsx-no-literals */
  }

  /**
   * Returns the string used to represent the title of the selected value.
   *
   * @returns {string} Typically a model name like User or Group
   */
  renderTitle() {
    switch (this.props.value.groupId) {
      case "archived-user":
        return i18n.t("report.users_archived");
      case "group":
        return i18n.t("activerecord.models.group.one");
      default:
        return i18n.t("activerecord.models.user.one");
    }
  }
}

FilterValue.propTypes = {
  onRemove: PropTypes.func,
  value: PropTypes.shape({
    groupId: PropTypes.string.isRequired,
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    label: PropTypes.string.isRequired
  }).isRequired
};

export default FilterValue;
